/**
 * The rules the top-level transition views are built by (burndown Phase 8),
 * apart from anything that renders.
 *
 * Not one diagram of everything: at a normal model's size that is the
 * requirements hierarchy's thin strip again. A table of every transition, a
 * focus on ONE def at a time, and an overview of the machines.
 */

import type { StateMap, StateMapTransition } from '../../schema'

export interface TransitionFilter {
  /** A def's id: transitions with either end a usage of it (or it). */
  definition?: string
  /** A machine's id: transitions inside it. */
  machine?: string
}

/** The transitions a filter keeps, in the map's order. */
export function filterTransitions(map: StateMap, filter: TransitionFilter): StateMapTransition[] {
  return map.transitions.filter((edge) =>
    (!filter.machine || edge.machine.id === filter.machine) &&
    (!filter.definition || edge.from?.definition === filter.definition || edge.to?.definition === filter.definition))
}

/** Every def an end of some transition is, by id, with its name - for a filter menu. */
export function definitionsIn(map: StateMap): { id: string; name: string }[] {
  const found = new Map<string, string>()
  for (const edge of map.transitions) {
    for (const end of [edge.from, edge.to]) {
      if (end) found.set(end.definition, end.definition_name)
    }
  }
  return [...found.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
}

/** One neighbour of a focused def: the def at the far end, the machine, and the edge. */
export interface FocusEdge {
  /** The def at the other end, by id, and its name. */
  definition: string
  name: string
  machine: string
  label: string
}

/**
 * A def's direct ins and outs across every machine that uses it - one step,
 * labelled by machine. A self-transition is both an in and an out.
 */
export function focusOf(map: StateMap, definition: string): { ins: FocusEdge[]; outs: FocusEdge[] } {
  const ins: FocusEdge[] = []
  const outs: FocusEdge[] = []
  const label = (edge: StateMapTransition) =>
    [edge.trigger, edge.guard ? `[${edge.guard}]` : ''].filter(Boolean).join(' ')
  for (const edge of map.transitions) {
    if (!edge.from || !edge.to) continue
    if (edge.to.definition === definition) {
      ins.push({ definition: edge.from.definition, name: edge.from.definition_name, machine: edge.machine.name, label: label(edge) })
    }
    if (edge.from.definition === definition) {
      outs.push({ definition: edge.to.definition, name: edge.to.definition_name, machine: edge.machine.name, label: label(edge) })
    }
  }
  return { ins, outs }
}

/** Where a machine's box goes on the overview: a grid, in the map's order. */
export function overviewPlaces(count: number, columns = 3): { x: number; y: number }[] {
  return Array.from({ length: count }, (_, index) => ({
    x: (index % columns) * 320,
    y: Math.floor(index / columns) * 200,
  }))
}
